import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  FileCheck,
  Car, 
  Calendar, 
  Euro, 
  Gauge, 
  Clock,
  AlertTriangle,
  CheckCircle2,
  MapPin
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Badge scadenza (fuori dal componente principale per evitare re-render)
const ScadenzaBadge = ({ mesiRimanenti, inScadenza, scaduto }) => {
  if (scaduto) {
    return (
      <Badge className="bg-red-100 text-red-700 border border-red-300">
        <AlertTriangle className="w-3 h-3 mr-1" />
        Contratto Scaduto
      </Badge>
    );
  }
  if (inScadenza) {
    return (
      <Badge className="bg-amber-100 text-amber-700 border border-amber-300">
        <Clock className="w-3 h-3 mr-1" />
        In scadenza tra {mesiRimanenti} mes{mesiRimanenti === 1 ? 'e' : 'i'}
      </Badge>
    );
  }
  return (
    <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300">
      <CheckCircle2 className="w-3 h-3 mr-1" />
      Attivo
    </Badge>
  );
};

const ContrattiClientePage = () => {
  const { getAuthHeader, user } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState(null);

  const fetchContracts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/cliente/contratti`, { 
        headers: getAuthHeader() 
      });
      setContracts(response.data.contracts || []);
      setContact(response.data.contact || null);
    } catch (error) {
      console.error('Error fetching contracts:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  // Formattazione data
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/D';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6" data-testid="contratti-cliente-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-800 flex items-center gap-3" style={{ fontFamily: 'Plus Jakarta Sans' }}>
          <FileCheck className="w-7 h-7 text-emerald-600" />
          I Miei Contratti
          {contracts.length > 0 && (
            <Badge className="bg-emerald-600 text-white text-lg px-3 py-1">
              {contracts.length}
            </Badge>
          )}
        </h1>
        <p className="text-slate-500 mt-1">
          {contact?.nome_cognome && `Ciao ${contact.nome_cognome.split(' ')[0]}, `}
          qui trovi i tuoi contratti di noleggio attivi
        </p>
      </div>

      {/* Contenuto */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2].map(i => (
            <div key={i} className="h-48 bg-slate-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : contracts.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <FileCheck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 font-medium">Nessun contratto attivo</p>
              <p className="text-sm text-slate-400 mt-1">
                Quando un&apos;offerta diventerà contratto, apparirà qui.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {contracts.map(contract => {
            const tipoVeicolo = contract.vehicle_info?.tipo || 'nuovo';
            const isUsato = tipoVeicolo === 'usato';
            
            return (
              <Card 
                key={contract.id} 
                className={`overflow-hidden border-2 ${
                  contract.scaduto 
                    ? 'border-red-300 bg-red-50/30'
                    : contract.in_scadenza 
                      ? 'border-amber-300 bg-amber-50/30'
                      : 'border-emerald-300 bg-emerald-50/30'
                }`}
                data-testid={`contract-${contract.id}`}
              >
                <CardContent className="p-0">
                  <div className="flex flex-col lg:flex-row">
                    {/* Sezione Veicolo */}
                    <div className="lg:w-1/3 p-6 border-b lg:border-b-0 lg:border-r border-slate-200/50">
                      {/* Immagine/Placeholder Veicolo */}
                      <div className="relative mb-4">
                        {contract.vehicle_info?.link_immagine ? (
                          <div className="w-full h-40 rounded-lg overflow-hidden bg-slate-100">
                            <iframe
                              src={contract.vehicle_info.link_immagine}
                              className="w-full h-full"
                              title={`${contract.vehicle_info?.marca} ${contract.vehicle_info?.modello}`}
                              frameBorder="0"
                              allowFullScreen
                            />
                          </div>
                        ) : (
                          <div className="w-full h-40 bg-gradient-to-br from-slate-100 to-slate-200 rounded-lg flex items-center justify-center">
                            <Car className="w-16 h-16 text-slate-300" />
                          </div>
                        )}
                        
                        {/* Badge Tipo */}
                        <div className="absolute top-2 left-2">
                          <Badge className={`${
                            isUsato 
                              ? 'bg-amber-500 text-white' 
                              : 'bg-emerald-500 text-white'
                          } text-xs px-2 py-0.5`}>
                            {tipoVeicolo.toUpperCase()}
                          </Badge>
                        </div>
                        
                        {/* Badge Stato */}
                        <div className="absolute top-2 right-2">
                          <ScadenzaBadge 
                            mesiRimanenti={contract.mesi_rimanenti}
                            inScadenza={contract.in_scadenza}
                            scaduto={contract.scaduto}
                          />
                        </div>
                      </div>
                      
                      {/* Info Veicolo */}
                      <h3 className="text-xl font-bold text-slate-800">
                        {contract.vehicle_info?.marca} {contract.vehicle_info?.modello}
                      </h3>
                      {contract.vehicle_info?.versione && (
                        <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                          {contract.vehicle_info.versione}
                        </p>
                      )}
                      
                      {/* Targa */}
                      {contract.vehicle_info?.targa && (
                        <div className="mt-3 inline-flex items-center gap-2 px-3 py-2 bg-slate-800 text-white rounded-lg">
                          <MapPin className="w-4 h-4" />
                          <span className="font-mono font-bold tracking-wider">
                            {contract.vehicle_info.targa}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Sezione Dati Contratto */}
                    <div className="lg:w-2/3 p-6">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {/* Canone Mensile */}
                        <div className="col-span-2 md:col-span-1 p-4 bg-white rounded-xl border border-slate-200">
                          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase mb-1">
                            <Euro className="w-4 h-4" />
                            Canone Mensile
                          </div>
                          <p className="text-2xl font-bold text-emerald-600">
                            €{contract.canone_mensile?.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                          </p>
                          <p className="text-xs text-slate-400">IVA esclusa</p>
                        </div>
                        
                        {/* Durata */}
                        <div className="p-4 bg-white rounded-xl border border-slate-200">
                          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase mb-1">
                            <Calendar className="w-4 h-4" />
                            Durata
                          </div>
                          <p className="text-xl font-bold text-slate-800">
                            {contract.durata_mesi} mesi
                          </p>
                          <p className="text-xs text-slate-400">
                            {contract.mesi_rimanenti} rimanenti
                          </p>
                        </div>
                        
                        {/* Km Totali */}
                        <div className="p-4 bg-white rounded-xl border border-slate-200">
                          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase mb-1">
                            <Gauge className="w-4 h-4" />
                            Km Inclusi
                          </div>
                          <p className="text-xl font-bold text-slate-800">
                            {contract.km_totali?.toLocaleString('it-IT')}
                          </p>
                          <p className="text-xs text-slate-400">
                            {contract.km_annui?.toLocaleString('it-IT')} km/anno
                          </p>
                        </div>
                        
                        {/* Data Inizio */}
                        <div className="p-4 bg-white rounded-xl border border-slate-200">
                          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase mb-1">
                            <Calendar className="w-4 h-4" />
                            Inizio Contratto
                          </div>
                          <p className="text-sm font-semibold text-slate-800">
                            {formatDate(contract.data_inizio)}
                          </p>
                        </div>
                        
                        {/* Data Fine */}
                        <div className={`p-4 rounded-xl border ${
                          contract.scaduto 
                            ? 'bg-red-50 border-red-200'
                            : contract.in_scadenza
                              ? 'bg-amber-50 border-amber-200'
                              : 'bg-white border-slate-200'
                        }`}>
                          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase mb-1">
                            <Calendar className="w-4 h-4" />
                            Fine Contratto
                          </div>
                          <p className={`text-sm font-semibold ${
                            contract.scaduto 
                              ? 'text-red-700'
                              : contract.in_scadenza
                                ? 'text-amber-700'
                                : 'text-slate-800'
                          }`}>
                            {formatDate(contract.data_fine)}
                          </p>
                        </div>
                        
                        {/* Anticipo (se presente) */}
                        {contract.anticipo > 0 && (
                          <div className="p-4 bg-white rounded-xl border border-slate-200">
                            <div className="flex items-center gap-2 text-slate-500 text-xs uppercase mb-1">
                              <Euro className="w-4 h-4" />
                              Anticipo Versato
                            </div>
                            <p className="text-sm font-semibold text-slate-800">
                              €{contract.anticipo?.toLocaleString('it-IT')}
                            </p>
                          </div>
                        )}
                      </div>
                      
                      {/* Info Agente */}
                      {contract.agent_name && (
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          <p className="text-xs text-slate-500">
                            Consulente: <span className="font-medium text-slate-700">{contract.agent_name}</span>
                            {contract.mese_competenza && (
                              <span className="ml-3">• Mese competenza: {contract.mese_competenza}</span>
                            )}
                          </p>
                        </div>
                      )}
                      
                      {/* Messaggio Scadenza */}
                      {contract.in_scadenza && !contract.scaduto && (
                        <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                          <p className="text-sm text-amber-800 flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            <span>
                              Il tuo contratto scadrà tra <strong>{contract.mesi_rimanenti} mes{contract.mesi_rimanenti === 1 ? 'e' : 'i'}</strong>. 
                              Verrai contattato dal tuo consulente per valutare le opzioni di rinnovo.
                            </span>
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ContrattiClientePage;
